import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { SERVICES } from "@/data/site";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";

const blank = (service, city) => ({ name: "", email: "", phone: "", service: service || "", city: city || "", message: "" });

export default function ContactForm({ service = "", city = "", heading = "Send us a message", subheading = "Fill in the form and we'll get back to you." }) {
  const [form, setForm] = useState(blank(service, city));
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { setForm((f) => ({ ...f, service: service || f.service, city: city || f.city })); }, [service, city]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Thank you! We'll reach out within 1 business day.");
      setForm(blank(service, city));
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check the form and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-3xl bg-[color:var(--dh-cream)] p-6 md:p-8 border border-[color:var(--dh-line)]" data-testid="contact-form">
      <div className="eyebrow">Get in Touch</div>
      <h2 className="mt-2 font-display text-2xl md:text-3xl text-[color:var(--dh-navy)]">{heading}</h2>
      <p className="text-sm text-[color:var(--dh-slate)] mt-1">{subheading}</p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Field label="Full name">
          <input required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="input-name" className="input" />
        </Field>
        <Field label="Email">
          <input type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="input-email" className="input" />
        </Field>
        <Field label="Phone">
          <input value={form.phone} onChange={set("phone")} placeholder="+91 …" data-testid="input-phone" className="input" />
        </Field>
        <Field label="Service">
          <select value={form.service} onChange={set("service")} data-testid="input-service" className="input">
            <option value="">Select a service</option>
            {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
          </select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="City">
            <input value={form.city} onChange={set("city")} placeholder="e.g. Dehradun" data-testid="input-city" className="input" maxLength={80} />
          </Field>
        </div>
      </div>

      <div className="mt-4">
        <Field label="Message">
          <textarea required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us a bit about your project…" data-testid="input-message" className="input resize-none" />
        </Field>
      </div>

      <button disabled={loading} className="btn-primary mt-6 w-full sm:w-auto justify-center" data-testid="submit-contact">
        {loading ? "Sending..." : (<>Send Message <Send className="w-4 h-4" /></>)}
      </button>

      <style>{`
        .input {
          width: 100%; padding: .8rem 1rem; border-radius: 12px;
          border: 1px solid var(--dh-line); background: #fff; font-size: 15px;
          color: var(--dh-ink); transition: border-color .2s ease, box-shadow .2s ease;
        }
        .input:focus { outline: none; border-color: var(--dh-orange); box-shadow: 0 0 0 3px rgba(255,107,26,0.15); }
      `}</style>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--dh-navy)] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
